import { queriesRequirements } from "@query";
import {
  PayeeRequirement,
  PayeeRequirementList,
  RequirementType
} from "@wingspanhq/wingspan-api-sdk";

import { WSQueryClient } from "../../../query/helpers";

/**
 * Updates cached tax verification requirements after W9 consent is given.
 * Uses QueryClient.getQueriesData to find and update ALL cached requirement
 * list entries — both single-page reads (a `PayeeRequirementList` envelope)
 * and the paginated/`useQueryAll` shape (`{ pages: PayeeRequirementList[] }`).
 * @param payerIds - Payer IDs that now have consent
 */
export function updateTaxVerificationRequirementCaches(payerIds: string[]) {
  const payerIdSet = new Set(payerIds);

  // Helper to update a single requirement if it matches criteria
  const updateRequirement = (req: PayeeRequirement): PayeeRequirement => {
    // Only update TaxVerification requirements for specified payers
    if (
      req.type === RequirementType.TAX_VERIFICATION &&
      req.payerAccountId &&
      payerIdSet.has(req.payerAccountId)
    ) {
      return {
        ...req,
        dataSourceMetadata: {
          ...req.dataSourceMetadata,
          payeeOwnedData: {
            ...req.dataSourceMetadata?.payeeOwnedData,
            // untyped metadata blob — "Allow" is the wire value the backend
            // stamps for a granted tax-document share
            shareTaxDocument: "Allow"
          }
        }
      };
    }
    return req;
  };

  const updateList = (list: PayeeRequirementList): PayeeRequirementList => ({
    ...list,
    data: list.data?.map(updateRequirement)
  });

  const listQueries = WSQueryClient.getQueriesData<
    PayeeRequirementList | { pages?: PayeeRequirementList[] }
  >({
    queryKey: queriesRequirements.listPayeeRequirements.queryKeyBase,
    exact: false // Match all queries starting with this key
  });

  listQueries.forEach(([queryKey, data]) => {
    if (!data) {
      return;
    }

    if ("pages" in data && Array.isArray(data.pages)) {
      WSQueryClient.setQueryData(queryKey, {
        ...data,
        pages: data.pages.map(updateList)
      });
    } else if ("data" in data && Array.isArray(data.data)) {
      WSQueryClient.setQueryData(queryKey, updateList(data));
    }
  });
}
